import React, { useEffect, useState } from "react";
import client, { apiError } from "@/lib/api";
import { Organization } from "@/lib/types";
import { PageHeader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Save, Building2 } from "lucide-react";
import { SecurityPanel } from "@/components/SecurityPanel";

const OrganizationPage: React.FC = () => {
  const [org, setOrg] = useState<Organization | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    client.get("/organization").then((r) => setOrg(r.data));
  }, []);

  const save = async () => {
    if (!org) return;
    setSaving(true);
    try {
      const res = await client.put("/organization", {
        name: org.name,
        timezone: org.timezone,
        tax_id: org.tax_id,
        notes: org.notes,
      });
      setOrg(res.data);
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!org)
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#DD0000]" />
      </div>
    );

  return (
    <div data-testid="organization-page">
      <PageHeader title="Negocio" subtitle="Configuración de la organización" />

      <div className="max-w-2xl border border-slate-200 bg-white p-6">
        <div className="mb-6 flex items-center gap-3 border-b border-slate-100 pb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-sm brand-gradient">
            <Building2 className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-semibold text-slate-800">{org.name}</div>
            <div className="text-xs text-slate-400">Un solo negocio · Moneda única</div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500">Nombre del negocio</Label>
            <Input
              value={org.name}
              onChange={(e) => setOrg({ ...org, name: e.target.value })}
              data-testid="org-name-input"
              className="mt-1.5 rounded-none border-slate-300"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Moneda</Label>
              <Input value={org.currency} disabled data-testid="org-currency" className="mt-1.5 rounded-none border-slate-200 bg-slate-50" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Zona horaria</Label>
              <Input
                value={org.timezone}
                onChange={(e) => setOrg({ ...org, timezone: e.target.value })}
                data-testid="org-timezone-input"
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">RFC / Tax ID</Label>
            <Input
              value={org.tax_id || ""}
              onChange={(e) => setOrg({ ...org, tax_id: e.target.value })}
              data-testid="org-taxid-input"
              className="mt-1.5 rounded-none border-slate-300"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Notas</Label>
            <Textarea
              value={org.notes || ""}
              onChange={(e) => setOrg({ ...org, notes: e.target.value })}
              data-testid="org-notes-input"
              rows={2}
              className="mt-1.5 rounded-none border-slate-300"
            />
          </div>

          <button
            onClick={save}
            disabled={saving}
            data-testid="org-save-button"
            className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
            Guardar cambios
          </button>
        </div>
      </div>

      <SecurityPanel />
    </div>
  );
};

export default OrganizationPage;
